import { useEffect, useRef } from 'preact/hooks'
import { useSignal } from '@preact/signals'
import type { ImageWidget } from 'apps/admin/widgets.ts'

interface Props {
  imagemA?: ImageWidget // imagem de fundo (sem batom)
  imagemB?: ImageWidget // imagem da frente (com batom)
  alt?: string
}

export default function ImageCompareClipIsland({
  imagemA,
  imagemB,
  alt = '',
}: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const sliderX = useSignal(50)

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return

    // MOUSE
    const onMouseMove = (e: MouseEvent) => {
      const bounds = wrapper.getBoundingClientRect()
      const x = e.clientX - bounds.left
      const percent = Math.max(0, Math.min((x / bounds.width) * 100, 100))
      sliderX.value = percent
    }

    const onMouseDown = () => {
      globalThis.addEventListener('mousemove', onMouseMove)
      globalThis.addEventListener('mouseup', onMouseUp)
    }

    const onMouseUp = () => {
      globalThis.removeEventListener('mousemove', onMouseMove)
      globalThis.removeEventListener('mouseup', onMouseUp)
    }

    wrapper.addEventListener('mousedown', onMouseDown)

    // TOUCH
    const onTouchMove = (e: TouchEvent) => {
      const bounds = wrapper.getBoundingClientRect()
      const x = e.touches[0].clientX - bounds.left
      const percent = Math.max(0, Math.min((x / bounds.width) * 100, 100))
      sliderX.value = percent
    }

    const onTouchStart = () => {
      globalThis.addEventListener('touchmove', onTouchMove)
      globalThis.addEventListener('touchend', onTouchEnd)
    }

    const onTouchEnd = () => {
      globalThis.removeEventListener('touchmove', onTouchMove)
      globalThis.removeEventListener('touchend', onTouchEnd)
    }

    wrapper.addEventListener('touchstart', onTouchStart)

    return () => {
      wrapper.removeEventListener('mousedown', onMouseDown)
      globalThis.removeEventListener('mousemove', onMouseMove)
      globalThis.removeEventListener('mouseup', onMouseUp)

      wrapper.removeEventListener('touchstart', onTouchStart)
      globalThis.removeEventListener('touchmove', onTouchMove)
      globalThis.removeEventListener('touchend', onTouchEnd)
    }
  }, [])

  return (
    <div
      ref={wrapperRef}
      className="relative w-full max-w-[1048px] mx-auto aspect-[1048/391] overflow-hidden select-none cursor-ew-resize"
    >
      {/* Textos Antes e Depois */}
      <div className="absolute top-1 left-1 z-[15] bg-white text-[#2C2824] text-[15.25px] font-normal leading-[18px] px-3 py-1 ">
        Antes
      </div>
      <div className="absolute top-1 right-1 z-[15] bg-white text-[#2C2824] text-[15.25px] font-normal leading-[18px] px-3 py-1 ">
        Depois
      </div>

      <img
        src={imagemA}
        alt={alt}
        className="absolute top-0 left-0 w-full h-full object-cover z-0"
        draggable={false}
      />

      <img
        src={imagemB}
        alt={alt}
        className="absolute top-0 left-0 w-full h-full object-cover z-10"
        style={{ clipPath: `inset(0 ${100 - sliderX.value}% 0 0)` }}
        draggable={false}
      />

      {/* Linha divisória */}
      <div
        className="absolute top-0 h-full w-[3px] bg-[#DA1884] border-l border-[#DA1884] z-20"
        style={{
          left: `${sliderX.value}%`,
          transform: 'translateX(-50%)',
        }}
      />

      {/* Ícone personalizado do cursor */}
      <div
        className="absolute z-[15]"
        style={{
          left: `${sliderX.value}%`,
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <svg
          width="40"
          height="41"
          viewBox="0 0 40 41"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect y="0.5" width="40" height="40" rx="20" fill="#DA1884" />
          <path
            d="M12.1873 15L13.5832 16.3958L9.45818 20.5208L13.5832 24.6458L12.1873 26.0417L6.6665 20.5208L12.1873 15Z"
            fill="white"
          />
          <path
            d="M27.8123 25.9987L26.4165 24.6029L30.5415 20.4779L26.4165 16.3529L27.8123 14.957L33.3332 20.4779L27.8123 25.9987Z"
            fill="white"
          />
        </svg>
      </div>
    </div>
  )
}
